import React from 'react';

import Data, { _F } from 'data';
import { getIconPath } from 'common';
import { useShift } from 'ui/modifiers';
import { formatHtml } from 'utils/format';
import { gemEffect } from './ItemTooltip';

function EngravingHeader({ id, level, points }) {
  const data = Data.engraving[id];
  return (
    <div className="lap-item-header">
      <div className="lap-engraving-icon" style={data.icon ? {backgroundImage: `url(${getIconPath(data.icon)}`} : null}>
        <div className="lap-engraving-frame"/>
      </div>
      <div className="lap-item-header-info">
        <div className="lap-engraving-type">
          {_F(data.class ? 'tip.name.enum_engravetype_class' : 'tip.name.enum_engravetype_normal')}
        </div>
        {!!data.class && (
          <div className="lap-engraving-class">
            {_F('sys.engrave.class_info', _F(Data.playerClasses[data.class]?.name))}
          </div>
        )}
        {!!level && (
          <div className="lap-engraving-level">
            {_F('sys.tooltip.engrave_active_level', level)}
          </div>
        )}
        {!!points && (
          <div className="lap-engraving-points">
            {_F('sys.tooltip.engrave_active_point', points)}
          </div>
        )}
      </div>
    </div>
  );
}

export function EngravingTooltip({ id, level, points }) {
  const data = Data.engraving[id];
  if (!data) return <div className="lap-GameTooltip">Unknown engraving</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{formatHtml(data.name)}</div>
      <div className="lap-item-body">
        <EngravingHeader id={id} level={level} points={points}/>
        {!!points && (
          <div className="lap-item-group">
            {_F('sys.tooltip.engrave_desc', formatHtml(data.name), points)}
          </div>
        )}
        <div className="lap-item-group">
          <div className="lap-stats-header lap-disabled">
            {_F('sys.tooltip.level_engrave_info')}
          </div>
          <div className={level && level !== 1 ? 'lap-disabled' : null}>{_F('sys.tooltip.level_engrave_value_true', 1, 5, formatHtml(data.desc[0]))}</div>
          <div className={level && level !== 2 ? 'lap-disabled' : null}>{_F('sys.tooltip.level_engrave_value_true', 2, 10, formatHtml(data.desc[1]))}</div>
          <div className={level && level !== 3 ? 'lap-disabled' : null}>{_F('sys.tooltip.level_engrave_value_true', 3, 15, formatHtml(data.desc[2]))}</div>
        </div>
      </div>
    </div>
  );
}

function SkillHeader({ skill }) {
  const data = Data.skills[skill.id];
  const category = Data.skillCategories[data.category];
  return (
    <div className="lap-item-header">
      <div className="lap-skill-icon" style={data.icon ? {backgroundImage: `url(${getIconPath(data.icon)}`} : null}/>
      <div className="lap-item-header-info">
        <div className="lap-skill-type">{_F(Data.skillTypes[data.type])}</div>
        {!data.awakening && <div className="lap-skill-category">{Data.strings[category] !== 'enumnull' && _F(category)}</div>}
        {!!data.awakening && <div className="lap-skill-category lap-skill-awakening">{_F('tip.name.enum_skillgrade_awakening')}</div>}
        <div className="lap-skill-cooldown">
          {!!data.cooltime && _F('tip.name.common_cooltime', Math.round(data.cooltime / 1000) + _F('tip.name.common_sec'))}
        </div>
      </div>
    </div>
  );
}

function gaugeCost(info, value) {
  if (!info || !value) return null;
  return [value, <>{_F(info.tip)} {Math.abs(value) / (info.factor || 1)} {_F(value > 0 ? 'tip.name.common_consumption' : 'tip.name.common_creation')}</>];
}

function SkillCost({ skill }) {
  const data = Data.skills[skill.id];
  const level = (skill.level || 1) - 1;
  const result = [
    gaugeCost({tip: 'tip.name.enum_stattype_mp'}, data.mana?.[level] || 0),
    gaugeCost(Data.gaugeTypes[data.class]?.[0], data.gauge0?.[level] || 0),
    gaugeCost(Data.gaugeTypes[data.class]?.[1], data.gauge1?.[level] || 0),
  ].filter(Boolean).sort((a, b) => b[0] - a[0]);
  if (!result.length) return null;
  return (
    <div>
      {result[0][1]}
      {!!result[1] && ', '}{result[1]?.[1] || null}
      {!!result[2] && ', '}{result[2]?.[1] || null}
    </div>
  );
}

const SkillEffect = ({ tip }) => <div>{_F('sys.tip.tooltip_desc_add_effect', _F(tip))}</div>;

const SkillTripod = ({ data, level, pvp }) => !!data && (
  <div className="lap-skill-tripod">
    <div className="lap-skill-tripod-icon">
      {!!data.icon && <span style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
    </div>
    <div className="lap-skill-tripod-effect">
      <p className="lap-skill-tripod-name">{formatHtml(data.name)}</p>
      <p>{_F(level >= data.levels ? 'sys.tooltip.skill_tripod_tip_level_max' : 'sys.tooltip.skill_tripod_tip_level', level)}</p>
      <p>{formatHtml((pvp && data.descPvp) || data.desc, level)}</p>
    </div>
  </div>
);

function SkillTripods({ skill, pvp }) {
  if (!skill.tripods) return null;
  const data = Data.skills[skill.id];
  if (!data.tripods) return null;
  return (
    <div className="lap-item-group">
      {Object.entries(skill.tripods).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([tripod, level]) => {
        const index = parseInt(tripod) - 1;
        if (skill.level < Math.floor(index / 3) * 3 + 4) return null;
        return <SkillTripod data={data.tripods[index]} level={level} key={tripod} pvp={pvp}/>;
      })}
    </div>
  );
}

function SkillRune({ skill }) {
  if (!skill.rune) return null;
  const data = Data.items[skill.rune];
  const rune = Data.runes[skill.rune];
  if (!data || !rune) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.skillrune_effect_skill')}
      </div>
      <div>
        [<span className={`la-color-${Data.itemGrades[data.grade].color}`}>{formatHtml(data.name)}</span>] {formatHtml(rune.desc)}
      </div>
    </div>
  );
}

function formatGemEffect(type, skill, value) {
  if (type === 5) return _F('tip.name.skill_damage', formatHtml(Data.skills[skill]?.name), value);
  if (type === 27) return _F('tip.name.skill_cooldown_reduction', formatHtml(Data.skills[skill]?.name), value);
  if (type === 34) return formatHtml(Data.skillGroup[skill]?.desc, value);
  if (type === 35) return formatHtml(Data.skillGroup[skill]?.desc, value);
  return null;
}

function SkillGem({ gem, skill }) {
  const data = Data.items[gem];
  const effect = data?.gem && gemEffect(`${data.gem.random}#${data.grade}`, skill);
  if (!effect) return null;
  const value = (effect.value / 100).toFixed(2);
  const res = _F('sys.tooltip.skill_effect_gem_option', formatGemEffect(effect.type, skill, value), '', _F(Data.playerClasses[Data.classMap[effect.class]]?.name));
  return (
    <div>
      {res}
    </div>
  );
}

function SkillGems({ skill }) {
  if (!skill.gems || !skill.gems.length) return null;
  return (
    <div className="lap-item-group">
      <div className="lap-stats-header">
        {_F('sys.tooltip.skill_item_option_title')}
      </div>
      {skill.gems.map((gem, index) => <SkillGem gem={gem?.[0]} skill={gem?.[1]} key={index}/>)}
    </div>
  );
}

const TripodLevel = ({ data, level, next, pvp }) => (
  <div className="lap-skill-tripod">
    <div className="lap-skill-tripod-icon">
      {!!data.icon && <span style={{backgroundImage: `url(${getIconPath(data.icon)})`}}/>}
    </div>
    <div className="lap-skill-tripod-effect">
      <div className="lap-skill-tripod-info">
        <div>{_F(pvp ? 'sys.tooltip.skill_onoff_pvp' : 'sys.tooltip.skill_onoff_pve')}</div>
        <div className="lap-skill-tripod-level">{_F('sys.tooltip.skill_tripod_level_max', data.levels)}</div>
      </div>
      <p className="lap-skill-tripod-name">{formatHtml(data.name)}</p>
      <p className="lap-skill-tripod-level">{_F(level >= data.levels ?
        (next ? 'sys.tooltip.skill_tripod_level_next_max' : 'sys.tooltip.skill_tripod_level_now_max') :
        (next ? 'sys.tooltip.skill_tripod_level_next' : 'sys.tooltip.skill_tripod_level_now'), level)}</p>
      <p>{formatHtml((pvp && data.descPvp) || data.desc, [level])}</p>
    </div>
  </div>
);

export function TripodTooltip({ skill, tripod, level, level2, pvp: pvpDefault }) {
  const data = Data.skills[skill]?.tripods?.[tripod - 1];
  const pvp = useShift() ? !pvpDefault : !!pvpDefault;
  if (!data) return <div className="lap-GameTooltip">Unknown tripod</div>;
  level = Math.max(1, Math.min(data.levels, parseInt(level || 1)));
  level2 = Math.max(1, Math.min(data.levels, parseInt(level2 || level)));
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-body">
        <TripodLevel data={data} level={level} pvp={pvp}/>
        {/*level2 !== level && <TripodLevel data={data} level={level2} pvp={pvp} next={true}/>*/}
      </div>
    </div>
  );
}

export default function SkillTooltip({ skill, pvp: pvpDefault }) {
  const data = Data.skills[skill.id];
  const pvp = useShift() ? !pvpDefault : !!pvpDefault;
  if (!data) return <div className="lap-GameTooltip">Unknown skill</div>;
  const level = skill.level || 1;
  const superArmor = (pvp ? data.superArmorPvp : data.superArmor);
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{formatHtml(data.name)}</div>
      <div className="lap-item-body">
        <SkillHeader skill={skill}/>
        <div className="lap-skill-mode">
          {_F(pvp ? 'sys.tooltip.skill_onoff_pvp' : 'sys.tooltip.skill_onoff_pve')}
        </div>
        <div className="lap-item-group">
          <div>{_F(level >= data.maxLevel ? 'tip.name.common_stats_level_max' : 'tip.name.common_stats_level', level)}</div>
          <SkillCost skill={skill}/>
        </div>
        <div className="lap-item-group">
          <div>{formatHtml((pvp && data.descPvp) || data.desc, level)}</div>
          {!!data.destruction && <SkillEffect tip={Data.skillDestructionType[data.destruction]}/>}
          {!!data.impairment && <SkillEffect tip={Data.skillImpairmentType[data.impairment]}/>}
          {!!data.directional && <SkillEffect tip={Data.skillDirectionalType[data.directional]}/>}
          {!!superArmor && <SkillEffect tip={Data.skillSuperArmorType[superArmor]}/>}
          {!!data.counter && <SkillEffect tip={'tip.name.enum_counterattacktooltiptype'}/>}
        </div>
        <SkillTripods skill={skill} pvp={pvp}/>
        <SkillRune skill={skill}/>
        <SkillGems skill={skill}/>
      </div>
    </div>
  );
}

export function SkillGroupTooltip({ id }) {
  const data = Data.skillGroup[id];
  if (!data) return <div className="lap-GameTooltip">Unknown skill</div>;
  return (
    <div className="lap-GameTooltip">
      <div className="lap-item-title">{formatHtml(data.name)}</div>
      <div className="lap-item-body">
        <div className="lap-item-header">
          <div className="lap-skill-icon" style={data.icon ? {backgroundImage: `url(${getIconPath(data.icon)}`} : null}/>
          <div className="lap-item-header-info"/>
        </div>
        <div className="lap-item-group">
          <div className="lap-stats-header">
            {_F('sys.tooltip.gem_skillgroup_target_title')}
          </div>
          <ul className="lap-bullet">
            {data.skills.map(id => {
              const skill = Data.skills[id];
              if (!skill || !skill.name) return null;
              const category = Data.skillCategories[skill.category];
              return (
                <li key={id} className="lap-skill-group-entry">
                  {_F('sys.tooltip.gem_skillgroup_target_index', Data.strings[category] !== 'enumnull' ? _F(category) : '', skill.name)}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
